import { Card, Flex, Image, Tag, theme, Tooltip } from 'antd';

import fallbackImage from '@assets/fallbackImage.jpg';
import BaseText from '@components/typography/BaseText';
import { useThumbnailUrl } from '@hooks/useThumbnailUrl';
import { PROJECT_TYPE_LABELS } from '@utils/constants';

/**
 * One object in the builder's taxonomy tree. A node without a model for this
 * project type can't be picked; it shows only to place the objects beneath it,
 * dimmed: disabled text and a faded thumbnail. (antd's disabled fill is a light
 * overlay, which on this dark page would raise the card rather than dim it.)
 *
 * @param {boolean} props.checked - the object is picked
 */
const BuilderTreeCard = ({ object, mode, checked = false }) => {
	const { token } = theme.useToken();
	const thumbnailUrl = useThumbnailUrl(object.thumbnail);
	const projectType = PROJECT_TYPE_LABELS[mode] ?? 'this project type';
	const disabled = !object.is_available;

	return (
		<Card
			size="small"
			// The resting border matches the fill, so only a checked card shows an edge.
			style={{
				width: '100%',
				borderColor: checked ? token.colorPrimary : token.colorBgContainer,
			}}>
			<Flex align="center" gap="small">
				<Image
					src={thumbnailUrl ?? fallbackImage}
					fallback={fallbackImage}
					alt=""
					preview={false}
					width={token.sizeXXL}
					height={token.sizeXXL}
					// antd has no disabled-opacity token; it dims a loading control this way.
					style={{
						objectFit: 'cover',
						flex: 'none',
						opacity: disabled ? token.opacityLoading : undefined,
					}}
				/>
				<Flex vertical flex={1} style={{ minWidth: 0 }}>
					<BaseText size="sm" strong ellipsis disabled={disabled}>
						{object.name}
					</BaseText>
					{object.description && (
						<BaseText size="xs" type="secondary" ellipsis disabled={disabled}>
							{object.description}
						</BaseText>
					)}
				</Flex>
				{disabled && (
					<Tooltip title={`No model is bound for ${projectType} yet`}>
						<Tag variant="outlined" style={{ marginInlineEnd: 0, flex: 'none' }}>
							No model
						</Tag>
					</Tooltip>
				)}
			</Flex>
		</Card>
	);
};

export default BuilderTreeCard;
